import React from "react";
import { Link } from "react-router-dom";
import { Heart, Star, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const inWish = has(product.id);
  const discount = Math.round(((product.mrp - product.sale) / product.mrp) * 100);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success("Added to cart", { description: product.title });
  };

  const handleWish = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(product.id);
    toast.success(inWish ? "Removed from wishlist" : "Added to wishlist");
  };

  return (
    <Link to={`/product/${product.slug}`} className="group block" data-testid={`product-card-${product.id}`}>
      <div className="relative aspect-[4/5] bg-neutral-100 rounded-2xl overflow-hidden">
        <img
          src={product.images[0]}
          alt={product.title}
          className="w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {product.images[1] && (
          <img
            src={product.images[1]}
            alt=""
            className="absolute inset-0 w-full h-full object-contain p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            loading="lazy"
          />
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.badge && (
            <span className="bg-neutral-900 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
              {product.badge}
            </span>
          )}
          {discount > 0 && (
            <span className="bg-[#FF5A1F] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
              -{discount}%
            </span>
          )}
        </div>
        <button
          onClick={handleWish}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition ${
            inWish ? "bg-[#FF5A1F] text-white" : "bg-white/90 hover:bg-white text-neutral-900"
          }`}
          data-testid={`wishlist-btn-${product.id}`}
          aria-label="Toggle wishlist"
        >
          <Heart className="w-4 h-4" fill={inWish ? "currentColor" : "none"} />
        </button>
        <button
          onClick={handleAdd}
          className="absolute bottom-3 left-3 right-3 h-10 rounded-full bg-neutral-900 text-white text-sm font-semibold md:opacity-0 md:translate-y-2 md:group-hover:opacity-100 md:group-hover:translate-y-0 md:transition-all md:duration-300 flex items-center justify-center gap-2 hover:bg-[#FF5A1F]"
          data-testid={`quick-add-${product.id}`}
        >
          <ShoppingBag className="w-4 h-4" /> <span className="hidden sm:inline">Quick </span>Add
        </button>
      </div>

      <div className="pt-3 md:pt-4 px-1">
        <div className="flex items-center gap-1 text-xs text-neutral-500 mb-1.5">
          <Star className="w-3.5 h-3.5 fill-[#FF5A1F] text-[#FF5A1F]" />
          <span className="font-semibold text-neutral-900">{product.rating}</span>
          <span>·</span>
          <span>{product.reviews}</span>
        </div>
        <h3 className="font-medium text-sm md:text-[15px] text-neutral-900 leading-snug line-clamp-2">
          {product.title}
        </h3>
        <div className="mt-1.5 md:mt-2 flex items-baseline gap-1.5 md:gap-2 flex-wrap">
          <span className="font-bold text-neutral-900 text-sm md:text-base">₹{product.sale}</span>
          <span className="text-xs md:text-sm text-neutral-400 line-through">₹{product.mrp}</span>
          {discount > 0 && <span className="text-[10px] md:text-xs font-semibold text-[#FF5A1F]">{discount}% off</span>}
        </div>
      </div>
    </Link>
  );
}
